import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import SignatureCanvas from 'react-signature-canvas';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme, styled } from '@mui/material/styles';

// ant design icons
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import CameraOutlined from '@ant-design/icons/CameraOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';

// project imports
import MainCard from 'components/MainCard';
import BodyDiagram from 'components/BodyDiagram';
import { saveDataEntry, FORM_TYPES, getEntriesForChild } from 'utils/offlineFirstDataLayer';
import { uploadDataCollectionFile, getClientDataCollections } from 'api/dataCollection';
import downscaleImageToDataUrl from 'utils/downscaleImage';

// A file <input> that stays IN the layout (visually hidden) instead of `display:none`
// (what the `hidden` attribute sets). iOS/iPadOS Safari frequently refuses to open the
// file picker for a display:none input — both when clicked via a wrapping <label> and
// when triggered programmatically with .click() — which is why photo/signature upload
// worked on desktop but did nothing on the tablet. Clip-hiding keeps it openable.
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  width: 1,
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  bottom: 0,
  left: 0
});

// Signature Pad Component with proper sizing
const SignaturePad = ({ signatureRef, onEnd, theme, signHereLabel }) => {
  const containerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 150 });

  const updateCanvasSize = useCallback(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      setCanvasSize({ width, height: 150 });
    }
  }, []);

  useEffect(() => {
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [updateCanvasSize]);

  return (
    <Box
      ref={containerRef}
      sx={{
        border: `2px solid ${theme.palette.grey[300]}`,
        borderRadius: 1,
        bgcolor: theme.palette.grey[50],
        position: 'relative',
        '&:hover': {
          borderColor: theme.palette.grey[400]
        }
      }}
    >
      <SignatureCanvas
        ref={signatureRef}
        backgroundColor="rgb(250, 250, 250)"
        penColor={theme.palette.grey[800]}
        canvasProps={{
          width: canvasSize.width,
          height: canvasSize.height,
          style: {
            touchAction: 'none',
            borderRadius: '4px'
          }
        }}
        onEnd={onEnd}
      />
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          color: theme.palette.grey[400],
          pointerEvents: 'none',
          userSelect: 'none'
        }}
      >
        {signHereLabel}
      </Typography>
    </Box>
  );
};

SignaturePad.propTypes = {
  signatureRef: PropTypes.object,
  onEnd: PropTypes.func,
  theme: PropTypes.object,
  signHereLabel: PropTypes.string
};

const InjuryReportMode = ({
  viewMode = 'individual',
  onDataChange,
  childName = '',
  childId = null,
  abcRecords = [],
  therapistName = '',
  therapistId = null,
  defaultReportType = 'client',
  filterByType = null,
  singleReportType = null,
  sessionId = null,
  orgId = null,
  centerId = null,
  initialReports = []
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // ABC Dropdown options (inside component for intl access)
  // eslint-disable-next-line no-unused-vars
  const commonAntecedents = [
    intl.formatMessage({ id: 'form.abc.antecedent.deniedAccess' }),
    intl.formatMessage({ id: 'form.abc.antecedent.difficultTask' }),
    intl.formatMessage({ id: 'form.abc.antecedent.transition' }),
    intl.formatMessage({ id: 'form.abc.antecedent.askedToWait' }),
    intl.formatMessage({ id: 'form.abc.antecedent.peerInteraction' }),
    intl.formatMessage({ id: 'form.abc.antecedent.changeInRoutine' }),
    intl.formatMessage({ id: 'form.abc.antecedent.loudNoise' }),
    intl.formatMessage({ id: 'form.abc.antecedent.toldNo' }),
    intl.formatMessage({ id: 'form.abc.antecedent.endPreferredActivity' }),
    intl.formatMessage({ id: 'common.other' })
  ];

  // eslint-disable-next-line no-unused-vars
  const commonBehaviors = [
    intl.formatMessage({ id: 'form.abc.behavior.tantrum' }),
    intl.formatMessage({ id: 'form.abc.behavior.aggression' }),
    intl.formatMessage({ id: 'form.abc.behavior.selfInjury' }),
    intl.formatMessage({ id: 'form.abc.behavior.propertyDestruction' }),
    intl.formatMessage({ id: 'form.abc.behavior.elopement' }),
    intl.formatMessage({ id: 'form.abc.behavior.nonCompliance' }),
    intl.formatMessage({ id: 'form.abc.behavior.stereotypy' }),
    intl.formatMessage({ id: 'common.other' })
  ];

  // eslint-disable-next-line no-unused-vars
  const commonConsequences = [
    intl.formatMessage({ id: 'form.abc.consequence.removedFromSituation' }),
    intl.formatMessage({ id: 'form.abc.consequence.givenItemActivity' }),
    intl.formatMessage({ id: 'form.abc.consequence.redirected' }),
    intl.formatMessage({ id: 'form.abc.consequence.ignored' }),
    intl.formatMessage({ id: 'form.abc.consequence.physicalPrompt' }),
    intl.formatMessage({ id: 'form.abc.consequence.verbalReprimand' }),
    intl.formatMessage({ id: 'form.abc.consequence.timeInSafeSpace' }),
    intl.formatMessage({ id: 'common.other' })
  ];

  // Internal form mode: 'list' or 'form' - used when in grid view to toggle between list and form
  const [internalFormMode, setInternalFormMode] = useState('list');

  // Tab value for grid view - 0: Data Entry, 1: Historical Data
  const [tabValue, setTabValue] = useState(0);

  // Helper function to check if a date is today
  const isToday = (dateValue) => {
    if (!dateValue) return false;
    const today = new Date();
    const checkDate = new Date(dateValue);
    return (
      checkDate.getDate() === today.getDate() &&
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Injury reports list
  const [injuryReports, setInjuryReports] = useState(initialReports);

  // Pagination for the Historical Data tab — show 5 per page.
  const HISTORICAL_PAGE_SIZE = 5;
  const [historicalVisibleCount, setHistoricalVisibleCount] = useState(HISTORICAL_PAGE_SIZE);

  // Read-only view dialog for historical entries.
  const [viewingReport, setViewingReport] = useState(null);
  const handleViewReport = (report) => setViewingReport(report);
  const handleCloseView = () => setViewingReport(null);

  // Map a raw_data blob (from backend records OR IndexedDB pending entries)
  // to the in-memory report shape the UI works with.
  const buildReportFromRaw = useCallback(
    (raw, fallbackId, fallbackTimestamp) => ({
      id: fallbackId,
      type: raw.reportType || raw.type || 'client',
      childName: raw.childName || childName,
      injuredName: raw.injuredLearnerName || raw.injuredStaffName || raw.injuredName || '',
      dateOfInjury: raw.dateOfInjury || fallbackTimestamp,
      timeOfInjury: raw.timeOfInjury || fallbackTimestamp,
      injuryLocations: raw.injuryLocations || [],
      incidentDescription: raw.incidentDescription || '',
      behavior: raw.behavior || '',
      firstAidAdministered: raw.firstAidAdministered || 'no',
      hospitalVisitNeeded: raw.hospitalVisitNeeded || 'no',
      status: 'completed',
      createdAt: fallbackTimestamp,
      // Keep the full raw_data so the view dialog can show every field
      // (body diagram, signatures, ABC fields, photos, etc.).
      _rawData: raw
    }),
    [childName]
  );

  // Load this child's injury reports from both the backend (synced records in
  // PostgreSQL) and IndexedDB (locally-saved entries that may not yet have
  // synced). Merging both is necessary because `saveDataEntry` writes to
  // IndexedDB first and only syncs to the backend after a 1s debounce — if the
  // therapist closes/reopens the modal in that window, a backend-only fetch
  // returns nothing and the just-saved report appears to have vanished.
  const loadAllReports = useCallback(async () => {
    if (!childId) return;
    let backendItems = [];
    try {
      const res = await getClientDataCollections(childId, {
        datasheet_type_code: 'injury_report',
        page_size: 200
      });
      backendItems = res?.items || [];
    } catch (err) {
      console.warn('[InjuryReportMode] Failed to load historical injury reports from backend:', err?.message);
    }

    let pendingItems = [];
    try {
      pendingItems = (await getEntriesForChild(FORM_TYPES.INJURY_REPORT, childId)) || [];
    } catch (err) {
      console.warn('[InjuryReportMode] Failed to read pending injury reports from IndexedDB:', err?.message);
    }

    const fromBackend = backendItems
      .map((record) => {
        const raw = record?.raw_data || {};
        return { ...buildReportFromRaw(raw, record.id, record.collected_at), _historical: true };
      })
      .filter((r) => r.dateOfInjury);

    // Only include IndexedDB entries that have NOT yet synced. Once an entry
    // syncs (syncStatus === 'synced' / synced === true), the backend record is
    // the source of truth — including the IndexedDB copy too would duplicate
    // it AND a too-aggressive content-based dedupe could accidentally drop a
    // distinct report with the same date+name. PENDING / DRAFT / FAILED /
    // SYNCING entries still belong here because the backend doesn't know
    // about them yet.
    const fromIndexedDB = pendingItems
      .filter((entry) => entry?.syncStatus !== 'synced' && !entry?.synced)
      .map((entry) => {
        const raw = entry?.data || {};
        const timestamp = entry?.sessionDate || entry?.createdAt || new Date().toISOString();
        return buildReportFromRaw(raw, `idb-${entry.id}`, timestamp);
      })
      .filter((r) => r.dateOfInjury);

    setInjuryReports([...fromIndexedDB, ...fromBackend]);
  }, [childId, buildReportFromRaw]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadAllReports();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAllReports]);

  // Get filtered reports based on filterByType prop
  const displayedReports = filterByType ? injuryReports.filter((r) => r.type === filterByType) : injuryReports;

  // Tab content:
  //   • Data Entry → today's reports only (focused on the active recording
  //     flow). Today's entries also appear in Historical Data so nothing
  //     gets hidden from the therapist when they look for a saved report.
  //   • Historical Data → every saved report (today + older), sorted
  //     newest-first, paginated.
  const currentReports = displayedReports.filter((r) => isToday(r.dateOfInjury));
  const historicalReports = [...displayedReports].sort((a, b) => {
    const ta = a?.dateOfInjury ? new Date(a.dateOfInjury).getTime() : 0;
    const tb = b?.dateOfInjury ? new Date(b.dateOfInjury).getTime() : 0;
    return tb - ta;
  });
  const visibleHistoricalReports = historicalReports.slice(0, historicalVisibleCount);

  // Currently editing report (null for new)
  const [editingReport, setEditingReport] = useState(null);

  // Saving state to prevent duplicate saves
  const [saving, setSaving] = useState(false);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // Form type selection - use singleReportType if locked, otherwise defaultReportType
  const [reportType, setReportType] = useState(singleReportType || defaultReportType);

  // Selected ABC record for linking
  // eslint-disable-next-line no-unused-vars
  const [selectedAbcRecord, setSelectedAbcRecord] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedStaffAbcRecord, setSelectedStaffAbcRecord] = useState(null);

  // Filter to only show today's ABC records
  const todaysAbcRecords = useMemo(() => {
    if (!abcRecords || abcRecords.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return abcRecords.filter((record) => {
      if (!record.date) return false;
      const recordDate = new Date(record.date);
      recordDate.setHours(0, 0, 0, 0);
      return recordDate.getTime() === today.getTime();
    });
  }, [abcRecords]);

  // Client Injury Form State
  const [clientForm, setClientForm] = useState({
    injuredLearnerName: childName,
    dateOfReport: new Date(),
    dateOfInjury: new Date(),
    timeOfInjury: new Date(),
    injuryLocations: [],
    incidentDescription: '',
    staffPresent: therapistName,
    relatedChildOrStaffInjury: 'no',
    relatedChildName: '',
    relatedStaffName: '',
    firstAidAdministered: 'no',
    firstAidByWhom: '',
    parentNotified: 'no',
    parentNotifiedDetails: '',
    hospitalVisitNeeded: 'no',
    hospitalDetails: '',
    // ABC Fields
    antecedent: '',
    behavior: '',
    consequence: '',
    duration: '',
    // Injury Photos (optional)
    injuryPhotos: [],
    // Environment/Damage Details (optional)
    environmentDamage: 'no',
    environmentDamageDescription: '',
    propertyDamaged: '',
    environmentDamageDetails: '',
    environmentDamageImages: []
  });

  // Staff Injury Form State
  const [staffForm, setStaffForm] = useState({
    injuredStaffName: '',
    dateOfReport: new Date(),
    dateOfInjury: new Date(),
    timeOfInjury: new Date(),
    injuryLocations: [],
    incidentDescription: '',
    witnessesPresent: '',
    firstAidAdministered: 'no',
    administratorNotified: 'no',
    emergencyContactNotified: 'no',
    emergencyContactNotifiedDetails: '',
    hospitalVisitNeeded: 'no',
    hospitalDetails: '',
    // ABC Fields
    antecedent: '',
    behavior: '',
    consequence: '',
    duration: '',
    // Injury Photos (optional)
    injuryPhotos: [],
    // Environment/Damage Details (optional)
    environmentDamage: 'no',
    environmentDamageDescription: '',
    propertyDamaged: '',
    environmentDamageDetails: '',
    environmentDamageImages: []
  });

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Notify parent that injury form has data (UI status only — no formData).
  // Injury reports use explicit Submit to save, so we only pass formData at
  // submit time.  Passing formData here would create a duplicate IndexedDB
  // entry that also gets synced, resulting in two records per submit.
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    const activeForm = reportType === 'client' ? clientForm : staffForm;
    const hasDescription = activeForm.incidentDescription && activeForm.incidentDescription.trim().length > 0;
    const hasInjuryLocations = activeForm.injuryLocations && activeForm.injuryLocations.length > 0;
    if (!hasDescription && !hasInjuryLocations) return;

    // Signal "has data" without sending formData — avoids duplicate entries
    if (onDataChange) onDataChange();
  }, [clientForm, staffForm, reportType]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Upload photo files to Azure and return photo objects with server paths.
   * Falls back to base64 encoding if the upload fails (offline scenario).
   */
  const uploadPhotos = useCallback(
    async (files, idPrefix = 'photo') => {
      const results = [];
      for (const file of files) {
        const photoId = `${idPrefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Normalise every selected photo to a standard JPEG *before* upload.
        // iPad/iPhone cameras deliver HEIC/HEIF (often labelled "image.jpg") and
        // multi-MB images that our storage/serving pipeline can't reliably render
        // — the thumbnail came back as a broken "?" box on tablets/phones while
        // desktop (real JPEGs) worked. A canvas round-trip guarantees a
        // universally-decodable JPEG and downsizes large photos. `preview` is a
        // local blob URL so the thumbnail renders instantly with no server
        // round-trip (it is stripped before the record is persisted).
        let uploadFile = file;
        let jpegDataUrl = null;
        let preview;
        try {
          const normalisedDataUrl = await downscaleImageToDataUrl(file, 1600, 0.85);
          // downscaleImageToDataUrl SILENTLY returns the original bytes when the
          // browser can't decode the source (e.g. HEIC on a non-WebKit engine).
          // Only trust it when it actually produced a JPEG — otherwise we'd upload
          // HEIC bytes mislabelled ".jpg" and render a broken "?" thumbnail. When
          // it didn't, upload the ORIGINAL untouched so the backend transcodes it,
          // and leave `preview` unset so the thumbnail uses the server JPEG url.
          if (normalisedDataUrl && normalisedDataUrl.startsWith('data:image/jpeg')) {
            jpegDataUrl = normalisedDataUrl;
            const jpegBlob = await (await fetch(jpegDataUrl)).blob();
            const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
            uploadFile = new File([jpegBlob], `${baseName}.jpg`, { type: 'image/jpeg' });
            preview = URL.createObjectURL(jpegBlob);
          } else {
            console.warn('[InjuryReportMode] Local JPEG normalise unavailable; deferring to server transcode.');
          }
        } catch (convErr) {
          console.warn('[InjuryReportMode] Image normalise failed, uploading original file:', convErr);
        }

        try {
          const res = await uploadDataCollectionFile(uploadFile, childId, 'injury-report');
          results.push({
            id: photoId,
            path: res.path,
            url: res.url,
            name: uploadFile.name,
            // Prefer the instant local blob; if local normalise failed, fall back
            // to the server url — the backend always returns a real JPEG now.
            preview: preview || res.url
          });
        } catch (err) {
          console.warn('[InjuryReportMode] Photo upload failed, storing as base64 (offline fallback):', err);
          // Offline fallback: prefer the already-normalised JPEG; else read the original.
          let base64 = jpegDataUrl;
          if (!base64) {
            base64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => resolve(null);
              reader.readAsDataURL(uploadFile);
            });
          }
          results.push({
            id: photoId,
            base64,
            name: uploadFile.name,
            preview: preview || base64
          });
        }
      }
      return results;
    },
    [childId]
  );

  // Signature Refs - Child Injury Form
  const adminSignatureRef = useRef(null);
  const parentSignatureRef = useRef(null);
  const directTherapistSignatureRef = useRef(null);

  // Signature Refs - Therapist Injury Form
  const therapistSignatureRef = useRef(null);
  const therapistAdminSignatureRef = useRef(null);

  // File input refs for signature upload
  const adminSignatureFileRef = useRef(null);
  const parentSignatureFileRef = useRef(null);
  const directTherapistSignatureFileRef = useRef(null);
  const therapistSignatureFileRef = useRef(null);
  const therapistAdminSignatureFileRef = useRef(null);

  // Body Diagram Refs for PDF export
  const clientBodyDiagramRef = useRef(null);
  const staffBodyDiagramRef = useRef(null);

  // Clear signature functions
  const clearSignature = (ref) => {
    if (ref.current) {
      ref.current.clear();
    }
  };

  // Upload signature image into the canvas
  const handleSignatureUpload = (signatureRef, fileInputRef) => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (signatureRef.current) {
        signatureRef.current.clear();
        signatureRef.current.fromDataURL(reader.result, { ratio: 1 });
        onDataChange && onDataChange();
      }
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-uploaded
    fileInputRef.current.value = '';
  };

  const handleClientFormChange = (field, value) => {
    setClientForm((prev) => ({ ...prev, [field]: value }));
    onDataChange && onDataChange();
  };

  const handleStaffFormChange = (field, value) => {
    setStaffForm((prev) => ({ ...prev, [field]: value }));
    onDataChange && onDataChange();
  };

  const handleReportTypeChange = (event) => {
    setReportType(event.target.value);
  };

  // Handle ABC record selection for client form
  // eslint-disable-next-line no-unused-vars
  const handleAbcRecordSelect = (event) => {
    const recordId = event.target.value;
    if (!recordId) {
      setSelectedAbcRecord(null);
      return;
    }
    const record = todaysAbcRecords.find((r) => r.id === recordId);
    if (record) {
      setSelectedAbcRecord(record);
      // Auto-fill the ABC fields in client form
      setClientForm((prev) => ({
        ...prev,
        antecedent: record.antecedent || '',
        behavior: record.behavior || '',
        consequence: record.consequence || '',
        duration: record.duration ? `${record.duration} minutes` : ''
      }));
      onDataChange && onDataChange();
    }
  };

  // Handle ABC record selection for staff form
  // eslint-disable-next-line no-unused-vars
  const handleStaffAbcRecordSelect = (event) => {
    const recordId = event.target.value;
    if (!recordId) {
      setSelectedStaffAbcRecord(null);
      return;
    }
    const record = todaysAbcRecords.find((r) => r.id === recordId);
    if (record) {
      setSelectedStaffAbcRecord(record);
      // Auto-fill the ABC fields in staff form
      setStaffForm((prev) => ({
        ...prev,
        antecedent: record.antecedent || '',
        behavior: record.behavior || '',
        consequence: record.consequence || '',
        duration: record.duration ? `${record.duration} minutes` : ''
      }));
      onDataChange && onDataChange();
    }
  };

  // Format ABC record for display in dropdown
  // eslint-disable-next-line no-unused-vars
  const formatAbcRecordLabel = (record) => {
    const time = record.time || '';
    const a = record.antecedent ? record.antecedent.substring(0, 30) : 'N/A';
    const b = record.behavior ? record.behavior.substring(0, 30) : 'N/A';
    const duration = record.duration ? `${record.duration}min` : '';
    return `${time} - A: ${a}${a.length >= 30 ? '...' : ''} | B: ${b}${b.length >= 30 ? '...' : ''}${duration ? ` | ${duration}` : ''}`;
  };

  const handleSubmit = async () => {
    // Prevent duplicate saves
    if (saving) {
      return;
    }

    setSaving(true);

    try {
      // Create new report object
      const newReport = {
        id: editingReport?.id || `inj-${Date.now()}`,
        type: reportType,
        childName: childName, // Always associate with current child's profile
        injuredName: reportType === 'client' ? clientForm.injuredLearnerName : staffForm.injuredStaffName,
        dateOfInjury: reportType === 'client' ? clientForm.dateOfInjury : staffForm.dateOfInjury,
        timeOfInjury: reportType === 'client' ? clientForm.timeOfInjury : staffForm.timeOfInjury,
        injuryLocations: reportType === 'client' ? clientForm.injuryLocations : staffForm.injuryLocations,
        incidentDescription: reportType === 'client' ? clientForm.incidentDescription : staffForm.incidentDescription,
        behavior: reportType === 'client' ? clientForm.behavior : staffForm.behavior,
        firstAidAdministered: reportType === 'client' ? clientForm.firstAidAdministered : staffForm.firstAidAdministered,
        hospitalVisitNeeded: reportType === 'client' ? clientForm.hospitalVisitNeeded : staffForm.hospitalVisitNeeded,
        status: 'completed',
        createdAt: editingReport?.createdAt || new Date()
      };

      if (editingReport) {
        // Update existing report
        setInjuryReports((prev) => prev.map((r) => (r.id === editingReport.id ? newReport : r)));
      } else {
        // Add new report
        setInjuryReports((prev) => [newReport, ...prev]);
      }

      // Capture signatures as base64 before saving
      const signatures = {};
      if (reportType === 'client') {
        if (adminSignatureRef.current && !adminSignatureRef.current.isEmpty())
          signatures.adminSignature = adminSignatureRef.current.toDataURL('image/png');
        if (parentSignatureRef.current && !parentSignatureRef.current.isEmpty())
          signatures.parentSignature = parentSignatureRef.current.toDataURL('image/png');
        if (directTherapistSignatureRef.current && !directTherapistSignatureRef.current.isEmpty())
          signatures.therapistSignature = directTherapistSignatureRef.current.toDataURL('image/png');
      } else {
        if (therapistSignatureRef.current && !therapistSignatureRef.current.isEmpty())
          signatures.injuredTherapistSignature = therapistSignatureRef.current.toDataURL('image/png');
        if (therapistAdminSignatureRef.current && !therapistAdminSignatureRef.current.isEmpty())
          signatures.adminSignature = therapistAdminSignatureRef.current.toDataURL('image/png');
      }

      // Save to IndexedDB for offline-first sync (includes signatures + photo paths in raw_data)
      // clientChildId embeds the owning child's UUID so the supervisor report can
      // reliably filter this record to the correct child without relying on name matching.
      // Drop transient in-session `preview` blob URLs from photo arrays before
      // persisting — blob: URLs are dead after reload, so only server path/url
      // (or offline base64) should be saved.
      const stripPreview = (arr) => (Array.isArray(arr) ? arr.map(({ preview, ...rest }) => rest) : arr);
      const activeForm = reportType === 'client' ? clientForm : staffForm;
      const fullFormData = {
        ...activeForm,
        injuryPhotos: stripPreview(activeForm.injuryPhotos),
        environmentDamageImages: stripPreview(activeForm.environmentDamageImages),
        reportType,
        ...signatures,
        clientChildId: childId
      };
      await saveDataEntry(FORM_TYPES.INJURY_REPORT, fullFormData, {
        childId,
        childName,
        therapistId,
        therapistName,
        sessionId,
        orgId,
        centerId,
        sessionDate: new Date().toISOString()
      });
      // Reset form and go back to list view to give user feedback that save succeeded
      resetForm();
      setEditingReport(null);
      setInternalFormMode('list');
      onDataChange && onDataChange();

      // Refresh the list from IndexedDB + backend so the just-saved entry
      // shows up immediately (the in-memory addition above gets the local
      // state in sync; this re-read also picks up the canonical record once
      // the background sync to PostgreSQL finishes).
      try {
        await loadAllReports();
      } catch (refreshErr) {
        console.warn('[InjuryReportMode] Post-save refresh failed:', refreshErr?.message);
      }
    } catch (error) {
      console.error('[InjuryReportMode] Failed to save injury report to IndexedDB:', error);
    } finally {
      setSaving(false);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setClientForm({
      injuredLearnerName: childName,
      dateOfReport: new Date(),
      dateOfInjury: new Date(),
      timeOfInjury: new Date(),
      injuryLocations: [],
      incidentDescription: '',
      staffPresent: therapistName,
      relatedChildOrStaffInjury: 'no',
      relatedChildName: '',
      relatedStaffName: '',
      firstAidAdministered: 'no',
      firstAidByWhom: '',
      parentNotified: 'no',
      parentNotifiedDetails: '',
      hospitalVisitNeeded: 'no',
      hospitalDetails: '',
      antecedent: '',
      behavior: '',
      consequence: '',
      duration: '',
      injuryPhotos: [],
      environmentDamage: 'no',
      environmentDamageDescription: '',
      environmentDamageDetails: '',
      environmentDamageImages: [],
      propertyDamaged: ''
    });
    setStaffForm({
      injuredStaffName: '',
      dateOfReport: new Date(),
      dateOfInjury: new Date(),
      timeOfInjury: new Date(),
      injuryLocations: [],
      incidentDescription: '',
      witnessesPresent: '',
      firstAidAdministered: 'no',
      administratorNotified: 'no',
      emergencyContactNotified: 'no',
      emergencyContactNotifiedDetails: '',
      hospitalVisitNeeded: 'no',
      hospitalDetails: '',
      antecedent: '',
      behavior: '',
      consequence: '',
      duration: '',
      injuryPhotos: [],
      environmentDamage: 'no',
      environmentDamageDescription: '',
      environmentDamageDetails: '',
      environmentDamageImages: [],
      propertyDamaged: ''
    });
    setSelectedAbcRecord(null);
    setSelectedStaffAbcRecord(null);
    setReportType(singleReportType || 'client');
  };

  // When the target child changes, wipe any in-progress form so that stale
  // toggle values from the previous child can never bleed into a new record.

  useEffect(() => {
    resetForm();
    setEditingReport(null);
    setInternalFormMode('list');
    // Intentionally runs only when the child changes; resetForm is stable enough here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  // Handle creating new report
  const handleNewReport = () => {
    resetForm();
    setEditingReport(null);
    setInternalFormMode('form');
  };

  // Handle editing existing report
  const handleEditReport = (report) => {
    setEditingReport(report);
    setReportType(report.type);

    if (report.type === 'client') {
      setClientForm((prev) => ({
        ...prev,
        injuredLearnerName: report.injuredName,
        dateOfInjury: report.dateOfInjury,
        timeOfInjury: report.timeOfInjury,
        injuryLocations: report.injuryLocations || [],
        incidentDescription: report.incidentDescription || '',
        behavior: report.behavior || '',
        firstAidAdministered: report.firstAidAdministered || 'no',
        hospitalVisitNeeded: report.hospitalVisitNeeded || 'no'
      }));
    } else {
      setStaffForm((prev) => ({
        ...prev,
        injuredStaffName: report.injuredName,
        dateOfInjury: report.dateOfInjury,
        timeOfInjury: report.timeOfInjury,
        injuryLocations: report.injuryLocations || [],
        incidentDescription: report.incidentDescription || '',
        behavior: report.behavior || '',
        firstAidAdministered: report.firstAidAdministered || 'no',
        hospitalVisitNeeded: report.hospitalVisitNeeded || 'no'
      }));
    }

    setInternalFormMode('form');
  };

  // Handle delete confirmation
  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    if (reportToDelete) {
      setInjuryReports((prev) => prev.filter((r) => r.id !== reportToDelete.id));
      onDataChange && onDataChange();
    }
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  // Go back to list view
  const handleBackToList = () => {
    resetForm();
    setEditingReport(null);
    setInternalFormMode('list');
  };

  // Export to PDF function
  const handleExportPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;
    const lineHeight = 7;
    const marginLeft = 20;
    const labelWidth = 60;

    // Helper function to add text
    const addText = (label, value) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(label, marginLeft, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value || 'N/A'), marginLeft + labelWidth, yPos);
      yPos += lineHeight;
    };

    // Helper function to add section header
    const addSectionHeader = (title) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      yPos += 5;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginLeft, yPos);
      doc.setLineWidth(0.5);
      doc.line(marginLeft, yPos + 2, pageWidth - marginLeft, yPos + 2);
      yPos += 10;
    };

    // Helper function to capture and add body diagram
    const addBodyDiagram = async (ref, title) => {
      if (ref.current) {
        try {
          // Check if we need a new page
          if (yPos > 150) {
            doc.addPage();
            yPos = 20;
          }

          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(title, marginLeft, yPos);
          yPos += 5;

          const canvas = await html2canvas(ref.current, {
            backgroundColor: '#ffffff',
            scale: 2
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = 170;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          doc.addImage(imgData, 'PNG', marginLeft, yPos, imgWidth, Math.min(imgHeight, 100));
          yPos += Math.min(imgHeight, 100) + 10;
        } catch (error) {
          console.error('Error capturing body diagram:', error);
        }
      }
    };

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('CHILD ACCIDENT / INCIDENT REPORT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Report Type
    const reportTypeLabel =
      reportType === 'client' ? 'Child Injury Report' : reportType === 'staff' ? 'Therapist Injury Report' : 'Child & Related Staff Injury';
    addText('Report Type:', reportTypeLabel);
    yPos += 5;

    // Child Injury Details
    if (reportType === 'client' || reportType === 'client-staff') {
      addSectionHeader('Child Injury Details');
      addText('Name of Injured Learner:', clientForm.injuredLearnerName);
      addText('Date of Report:', clientForm.dateOfReport ? format(clientForm.dateOfReport, 'MMM dd, yyyy') : '');
      addText('Date of Injury:', clientForm.dateOfInjury ? format(clientForm.dateOfInjury, 'MMM dd, yyyy') : '');
      addText('Time of Injury:', clientForm.timeOfInjury ? format(clientForm.timeOfInjury, 'hh:mm a') : '');
      addText('Injury Locations:', clientForm.injuryLocations?.join(', ') || 'None marked');

      // Add Body Diagram
      await addBodyDiagram(clientBodyDiagramRef, 'Injury Location on Body:');

      // Incident Description (multi-line)
      yPos += 3;
      doc.setFont('helvetica', 'bold');
      doc.text('Incident Description:', marginLeft, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'normal');
      const descLines = doc.splitTextToSize(clientForm.incidentDescription || 'N/A', pageWidth - marginLeft * 2);
      doc.text(descLines, marginLeft, yPos);
      yPos += descLines.length * 5 + 5;

      addText('Staff Present:', clientForm.staffPresent);

      addText('Related Injury:', clientForm.relatedChildOrStaffInjury === 'yes' ? 'Yes' : 'No');
      if (clientForm.relatedChildOrStaffInjury === 'yes') {
        addText('Related Child Name:', clientForm.relatedChildName);
        addText('Related Staff Name:', clientForm.relatedStaffName);
      }
      addText('First Aid Administered:', clientForm.firstAidAdministered === 'yes' ? 'Yes' : 'No');
      if (clientForm.firstAidAdministered === 'yes') {
        addText('First Aid By:', clientForm.firstAidByWhom);
      }
      addText('Parent Notified:', clientForm.parentNotified === 'yes' ? 'Yes' : 'No');
      if (clientForm.parentNotified === 'yes' && clientForm.parentNotifiedDetails) {
        addText('Parent/Contact Details:', clientForm.parentNotifiedDetails);
      }
      addText('Hospital Visit Needed:', clientForm.hospitalVisitNeeded === 'yes' ? 'Yes' : 'No');
      if (clientForm.hospitalVisitNeeded === 'yes') {
        addText('Hospital Details:', clientForm.hospitalDetails);
      }

      // Signatures section
      yPos += 10;
      addSectionHeader('Signatures');

      // Add signature images if available - vertical layout
      const sigWidth = 60;
      const sigHeight = 30;

      // Administrator signature
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text("Administrator's Signature:", marginLeft, yPos);
      yPos += 3;
      if (adminSignatureRef.current && !adminSignatureRef.current.isEmpty()) {
        const adminSigData = adminSignatureRef.current.toDataURL('image/png');
        doc.addImage(adminSigData, 'PNG', marginLeft, yPos, sigWidth, sigHeight);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.line(marginLeft, yPos + 15, marginLeft + sigWidth, yPos + 15);
      }
      yPos += sigHeight + 10;

      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      // Parent signature
      doc.setFont('helvetica', 'bold');
      doc.text("Parent's Signature:", marginLeft, yPos);
      yPos += 3;
      if (parentSignatureRef.current && !parentSignatureRef.current.isEmpty()) {
        const parentSigData = parentSignatureRef.current.toDataURL('image/png');
        doc.addImage(parentSigData, 'PNG', marginLeft, yPos, sigWidth, sigHeight);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.line(marginLeft, yPos + 15, marginLeft + sigWidth, yPos + 15);
      }
      yPos += sigHeight + 10;

      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      // Direct Therapist signature
      doc.setFont('helvetica', 'bold');
      doc.text("Direct Therapist's Signature:", marginLeft, yPos);
      yPos += 3;
      if (directTherapistSignatureRef.current && !directTherapistSignatureRef.current.isEmpty()) {
        const therapistSigData = directTherapistSignatureRef.current.toDataURL('image/png');
        doc.addImage(therapistSigData, 'PNG', marginLeft, yPos, sigWidth, sigHeight);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.line(marginLeft, yPos + 15, marginLeft + sigWidth, yPos + 15);
      }
      yPos += sigHeight + 10;
    }

    // Therapist Injury Details
    if (reportType === 'staff' || reportType === 'client-staff') {
      if (reportType === 'client-staff') {
        doc.addPage();
        yPos = 20;
      }

      addSectionHeader('Therapist Injury Details');
      addText('Name of Injured Therapist:', staffForm.injuredStaffName);
      addText('Date of Report:', staffForm.dateOfReport ? format(staffForm.dateOfReport, 'MMM dd, yyyy') : '');
      addText('Date of Injury:', staffForm.dateOfInjury ? format(staffForm.dateOfInjury, 'MMM dd, yyyy') : '');
      addText('Time of Injury:', staffForm.timeOfInjury ? format(staffForm.timeOfInjury, 'hh:mm a') : '');
      addText('Injury Locations:', staffForm.injuryLocations?.join(', ') || 'None marked');

      // Add Body Diagram for therapist
      await addBodyDiagram(staffBodyDiagramRef, 'Injury Location on Body:');

      // Incident Description
      yPos += 3;
      doc.setFont('helvetica', 'bold');
      doc.text('Incident Description:', marginLeft, yPos);
      yPos += lineHeight;
      doc.setFont('helvetica', 'normal');
      const staffDescLines = doc.splitTextToSize(staffForm.incidentDescription || 'N/A', pageWidth - marginLeft * 2);
      doc.text(staffDescLines, marginLeft, yPos);
      yPos += staffDescLines.length * 5 + 5;

      addText('Witnesses Present:', staffForm.witnessesPresent);

      addText('First Aid Administered:', staffForm.firstAidAdministered === 'yes' ? 'Yes' : 'No');
      addText('Administrator Notified:', staffForm.administratorNotified === 'yes' ? 'Yes' : 'No');
      addText('Emergency Contact Notified:', staffForm.emergencyContactNotified === 'yes' ? 'Yes' : 'No');
      if (staffForm.emergencyContactNotified === 'yes' && staffForm.emergencyContactNotifiedDetails) {
        addText('Emergency Contact Details:', staffForm.emergencyContactNotifiedDetails);
      }
      addText('Hospital Visit Needed:', staffForm.hospitalVisitNeeded === 'yes' ? 'Yes' : 'No');
      if (staffForm.hospitalVisitNeeded === 'yes') {
        addText('Hospital Details:', staffForm.hospitalDetails);
      }

      // Therapist form signatures - vertical layout
      yPos += 10;
      addSectionHeader('Signatures');

      const staffSigWidth = 60;
      const staffSigHeight = 30;

      // Injured Therapist signature
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text("Injured Therapist's Signature:", marginLeft, yPos);
      yPos += 3;
      if (therapistSignatureRef.current && !therapistSignatureRef.current.isEmpty()) {
        const sigData = therapistSignatureRef.current.toDataURL('image/png');
        doc.addImage(sigData, 'PNG', marginLeft, yPos, staffSigWidth, staffSigHeight);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.line(marginLeft, yPos + 15, marginLeft + staffSigWidth, yPos + 15);
      }
      yPos += staffSigHeight + 10;

      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      // Administrator signature
      doc.setFont('helvetica', 'bold');
      doc.text("Administrator's Signature:", marginLeft, yPos);
      yPos += 3;
      if (therapistAdminSignatureRef.current && !therapistAdminSignatureRef.current.isEmpty()) {
        const sigData = therapistAdminSignatureRef.current.toDataURL('image/png');
        doc.addImage(sigData, 'PNG', marginLeft, yPos, staffSigWidth, staffSigHeight);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.line(marginLeft, yPos + 15, marginLeft + staffSigWidth, yPos + 15);
      }
      yPos += staffSigHeight + 10;
    }

    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generated on ${format(new Date(), 'MMM dd, yyyy hh:mm a')}`, pageWidth / 2, 285, { align: 'center' });

    // Save the PDF
    const fileName = `Injury_Report_${clientForm.injuredLearnerName || staffForm.injuredStaffName || 'Unknown'}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    doc.save(fileName);
  };

  // Render Child Injury Form
  const renderClientInjuryForm = () => (
    <MainCard title={intl.formatMessage({ id: 'form.injuryReport.childInjuryDetails' })} sx={{ mb: 3 }}>
      <Grid container spacing={3}>
        {/* Row 1: Name and Report Date */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.injuryReport.nameOfInjuredLearner' })}
            value={clientForm.injuredLearnerName}
            onChange={(e) => handleClientFormChange('injuredLearnerName', e.target.value)}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={intl.formatMessage({ id: 'form.injuryReport.dateOfReport' })}
              value={clientForm.dateOfReport}
              onChange={(date) => handleClientFormChange('dateOfReport', date)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Row 2: Injury Date and Time */}
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={intl.formatMessage({ id: 'form.injuryReport.dateOfInjury' })}
              value={clientForm.dateOfInjury}
              onChange={(date) => handleClientFormChange('dateOfInjury', date)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              label={intl.formatMessage({ id: 'form.injuryReport.timeOfInjury' })}
              value={clientForm.timeOfInjury}
              onChange={(time) => handleClientFormChange('timeOfInjury', time)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Row 3: Injury Location - Body Diagram */}
        <Grid size={{ xs: 12 }}>
          <Box ref={clientBodyDiagramRef}>
            <BodyDiagram
              selectedParts={clientForm.injuryLocations}
              onChange={(parts) => handleClientFormChange('injuryLocations', parts)}
              onDescriptionAdd={(bodyPart, description) => {
                const entry = `${bodyPart}: ${description}`;
                const current = clientForm.incidentDescription;
                handleClientFormChange('incidentDescription', current ? `${current}\n${entry}` : entry);
              }}
            />
          </Box>
        </Grid>

        {/* Row 4: Incident Description */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={intl.formatMessage({ id: 'form.injuryReport.describeIncident' })}
            value={clientForm.incidentDescription}
            onChange={(e) => handleClientFormChange('incidentDescription', e.target.value)}
            required
            placeholder={intl.formatMessage({ id: 'form.injuryReport.placeholderDescriptionClient' })}
          />
        </Grid>

        {/* Row 5: Staff Present */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.injuryReport.staffPresent' })}
            value={clientForm.staffPresent}
            onChange={(e) => handleClientFormChange('staffPresent', e.target.value)}
            required
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Row 6: Related Injury */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.relatedInjuryQuestion' })}</Typography>
            <Switch
              checked={clientForm.relatedChildOrStaffInjury === 'yes'}
              onChange={(e) => handleClientFormChange('relatedChildOrStaffInjury', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {clientForm.relatedChildOrStaffInjury === 'yes' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'form.injuryReport.relatedChildName' })}
                value={clientForm.relatedChildName}
                onChange={(e) => handleClientFormChange('relatedChildName', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'form.injuryReport.relatedTherapistName' })}
                value={clientForm.relatedStaffName}
                onChange={(e) => handleClientFormChange('relatedStaffName', e.target.value)}
              />
            </Grid>
          </>
        )}

        {/* Row 7: First Aid */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.firstAidQuestion' })}</Typography>
            <Switch
              checked={clientForm.firstAidAdministered === 'yes'}
              onChange={(e) => handleClientFormChange('firstAidAdministered', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {clientForm.firstAidAdministered === 'yes' && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'form.injuryReport.firstAidByWhom' })}
              value={clientForm.firstAidByWhom}
              onChange={(e) => handleClientFormChange('firstAidByWhom', e.target.value)}
            />
          </Grid>
        )}

        {/* Row 8: Parent Notified */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.parentNotifiedQuestion' })}</Typography>
            <Switch
              checked={clientForm.parentNotified === 'yes'}
              onChange={(e) => handleClientFormChange('parentNotified', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {clientForm.parentNotified === 'yes' && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={intl.formatMessage({ id: 'form.injuryReport.parentContactDetails' })}
              placeholder={intl.formatMessage({ id: 'form.injuryReport.placeholderParentContact' })}
              value={clientForm.parentNotifiedDetails}
              onChange={(e) => handleClientFormChange('parentNotifiedDetails', e.target.value)}
            />
          </Grid>
        )}

        {/* Row 9: Hospital Visit */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.hospitalQuestion' })}</Typography>
            <Switch
              checked={clientForm.hospitalVisitNeeded === 'yes'}
              onChange={(e) => handleClientFormChange('hospitalVisitNeeded', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {clientForm.hospitalVisitNeeded === 'yes' && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={intl.formatMessage({ id: 'form.injuryReport.hospitalDetailsClient' })}
              value={clientForm.hospitalDetails}
              onChange={(e) => handleClientFormChange('hospitalDetails', e.target.value)}
            />
          </Grid>
        )}

        {/* Injury Photos Section (Optional) */}
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.injuryPhotos' })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {intl.formatMessage({ id: 'form.injuryReport.injuryPhotosHint' })}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Stack spacing={2}>
            <Button variant="outlined" component="label" startIcon={<CameraOutlined />} sx={{ width: 'fit-content' }}>
              {intl.formatMessage({ id: 'form.injuryReport.uploadPhoto' })}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  const uploaded = await uploadPhotos(files, 'photo');
                  handleClientFormChange('injuryPhotos', [...clientForm.injuryPhotos, ...uploaded]);
                }}
              />
            </Button>
            {clientForm.injuryPhotos.length > 0 && (
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {clientForm.injuryPhotos.map((photo) => (
                  <Box
                    key={photo.id}
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box
                      component="img"
                      src={photo.preview || photo.url || photo.base64}
                      alt={photo.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'error.lighter' }
                      }}
                      onClick={() => {
                        handleClientFormChange(
                          'injuryPhotos',
                          clientForm.injuryPhotos.filter((p) => p.id !== photo.id)
                        );
                      }}
                    >
                      <CloseOutlined style={{ fontSize: '0.75rem' }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Grid>

        {/* Environment/Damage Details Section (Optional) */}
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.environmentDamage' })}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.environmentDamageQuestion' })}</Typography>
            <Switch
              checked={clientForm.environmentDamage === 'yes'}
              onChange={(e) => handleClientFormChange('environmentDamage', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {clientForm.environmentDamage === 'yes' && (
          <>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={intl.formatMessage({ id: 'form.injuryReport.environmentDamageDetails' })}
                value={clientForm.environmentDamageDetails}
                onChange={(e) => handleClientFormChange('environmentDamageDetails', e.target.value)}
                placeholder={intl.formatMessage({ id: 'form.injuryReport.placeholderEnvironmentDamage' })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {intl.formatMessage({ id: 'form.injuryReport.environmentDamageImages' })}
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CameraOutlined />}
                  sx={{ width: 'fit-content' }}
                  disabled={(clientForm.environmentDamageImages || []).length >= 5}
                >
                  {(clientForm.environmentDamageImages || []).length >= 5
                    ? intl.formatMessage({ id: 'form.injuryReport.maxPhotosReached' })
                    : intl.formatMessage({ id: 'form.injuryReport.uploadPhoto' })}
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      // Guard the ORIGINAL file only against pathological sizes — uploadPhotos
                      // downscales every photo to a ~sub-MB JPEG before upload, so raw iPhone/iPad
                      // camera files (routinely 3–8 MB) must not be rejected here.
                      const maxSize = 30 * 1024 * 1024; // 30MB raw
                      const remainingSlots = 5 - (clientForm.environmentDamageImages || []).length;

                      const validFiles = files
                        .filter((file) => {
                          if (file.size > maxSize) {
                            alert(`File "${file.name}" is too large and was not added.`);
                            return false;
                          }
                          return true;
                        })
                        .slice(0, remainingSlots);

                      if (files.length > remainingSlots) {
                        alert(`Only ${remainingSlots} more photo(s) can be added. Extra files were ignored.`);
                      }

                      const uploaded = await uploadPhotos(validFiles, 'env-photo');
                      handleClientFormChange('environmentDamageImages', [...(clientForm.environmentDamageImages || []), ...uploaded]);
                    }}
                  />
                </Button>
                {(clientForm.environmentDamageImages || []).length > 0 && (
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {(clientForm.environmentDamageImages || []).map((photo) => (
                      <Box
                        key={photo.id}
                        sx={{
                          position: 'relative',
                          width: 100,
                          height: 100,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box
                          component="img"
                          src={photo.preview || photo.url || photo.base64}
                          alt={photo.name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'error.lighter' }
                          }}
                          onClick={() => {
                            handleClientFormChange(
                              'environmentDamageImages',
                              (clientForm.environmentDamageImages || []).filter((p) => p.id !== photo.id)
                            );
                          }}
                        >
                          <CloseOutlined style={{ fontSize: '0.75rem' }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Grid>
          </>
        )}

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            {intl.formatMessage({ id: 'form.injuryReport.signatures' })}
          </Typography>
        </Grid>

        {/* Administrator's Signature */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.adminSignature' })}
          </Typography>
          <SignaturePad
            signatureRef={adminSignatureRef}
            onEnd={() => onDataChange && onDataChange()}
            theme={theme}
            signHereLabel={intl.formatMessage({ id: 'form.injuryReport.signHere' })}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <Button size="small" onClick={() => clearSignature(adminSignatureRef)}>
              {intl.formatMessage({ id: 'form.injuryReport.clearSignature' })}
            </Button>
            <Button size="small" startIcon={<UploadOutlined />} onClick={() => adminSignatureFileRef.current?.click()}>
              {intl.formatMessage({ id: 'form.injuryReport.uploadSignature' })}
            </Button>
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              ref={adminSignatureFileRef}
              onChange={() => handleSignatureUpload(adminSignatureRef, adminSignatureFileRef)}
            />
          </Stack>
        </Grid>

        {/* Parent's Signature */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.parentSignature' })}
          </Typography>
          <SignaturePad
            signatureRef={parentSignatureRef}
            onEnd={() => onDataChange && onDataChange()}
            theme={theme}
            signHereLabel={intl.formatMessage({ id: 'form.injuryReport.signHere' })}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <Button size="small" onClick={() => clearSignature(parentSignatureRef)}>
              {intl.formatMessage({ id: 'form.injuryReport.clearSignature' })}
            </Button>
            <Button size="small" startIcon={<UploadOutlined />} onClick={() => parentSignatureFileRef.current?.click()}>
              {intl.formatMessage({ id: 'form.injuryReport.uploadSignature' })}
            </Button>
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              ref={parentSignatureFileRef}
              onChange={() => handleSignatureUpload(parentSignatureRef, parentSignatureFileRef)}
            />
          </Stack>
        </Grid>

        {/* Direct Therapist's Signature */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.directTherapistSignature' })}
          </Typography>
          <SignaturePad
            signatureRef={directTherapistSignatureRef}
            onEnd={() => onDataChange && onDataChange()}
            theme={theme}
            signHereLabel={intl.formatMessage({ id: 'form.injuryReport.signHere' })}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <Button size="small" onClick={() => clearSignature(directTherapistSignatureRef)}>
              {intl.formatMessage({ id: 'form.injuryReport.clearSignature' })}
            </Button>
            <Button size="small" startIcon={<UploadOutlined />} onClick={() => directTherapistSignatureFileRef.current?.click()}>
              {intl.formatMessage({ id: 'form.injuryReport.uploadSignature' })}
            </Button>
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              ref={directTherapistSignatureFileRef}
              onChange={() => handleSignatureUpload(directTherapistSignatureRef, directTherapistSignatureFileRef)}
            />
          </Stack>
        </Grid>
      </Grid>
    </MainCard>
  );

  // Render Therapist Injury Form
  const renderStaffInjuryForm = () => (
    <MainCard title={intl.formatMessage({ id: 'form.injuryReport.therapistInjuryDetails' })} sx={{ mb: 3 }}>
      <Grid container spacing={3}>
        {/* Row 1: Name and Report Date */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.injuryReport.nameOfInjuredTherapist' })}
            value={staffForm.injuredStaffName}
            onChange={(e) => handleStaffFormChange('injuredStaffName', e.target.value)}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={intl.formatMessage({ id: 'form.injuryReport.dateOfReport' })}
              value={staffForm.dateOfReport}
              onChange={(date) => handleStaffFormChange('dateOfReport', date)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Row 2: Injury Date and Time */}
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={intl.formatMessage({ id: 'form.injuryReport.dateOfInjury' })}
              value={staffForm.dateOfInjury}
              onChange={(date) => handleStaffFormChange('dateOfInjury', date)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              label={intl.formatMessage({ id: 'form.injuryReport.timeOfInjury' })}
              value={staffForm.timeOfInjury}
              onChange={(time) => handleStaffFormChange('timeOfInjury', time)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Row 3: Injury Location - Body Diagram */}
        <Grid size={{ xs: 12 }}>
          <Box ref={staffBodyDiagramRef}>
            <BodyDiagram
              selectedParts={staffForm.injuryLocations}
              onChange={(parts) => handleStaffFormChange('injuryLocations', parts)}
              onDescriptionAdd={(bodyPart, description) => {
                const entry = `${bodyPart}: ${description}`;
                const current = staffForm.incidentDescription;
                handleStaffFormChange('incidentDescription', current ? `${current}\n${entry}` : entry);
              }}
            />
          </Box>
        </Grid>

        {/* Row 4: Incident Description */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={intl.formatMessage({ id: 'form.injuryReport.describeIncident' })}
            value={staffForm.incidentDescription}
            onChange={(e) => handleStaffFormChange('incidentDescription', e.target.value)}
            required
            placeholder={intl.formatMessage({ id: 'form.injuryReport.placeholderDescriptionStaff' })}
          />
        </Grid>

        {/* Row 5: Witnesses Present */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={intl.formatMessage({ id: 'form.injuryReport.witnessesPresent' })}
            value={staffForm.witnessesPresent}
            onChange={(e) => handleStaffFormChange('witnessesPresent', e.target.value)}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Row 6: First Aid */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.firstAidQuestion' })}</Typography>
            <Switch
              checked={staffForm.firstAidAdministered === 'yes'}
              onChange={(e) => handleStaffFormChange('firstAidAdministered', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {/* Row 7: Administrator Notified */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.adminNotifiedQuestion' })}</Typography>
            <Switch
              checked={staffForm.administratorNotified === 'yes'}
              onChange={(e) => handleStaffFormChange('administratorNotified', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {/* Row 8: Emergency Contact Notified */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.emergencyContactQuestion' })}</Typography>
            <Switch
              checked={staffForm.emergencyContactNotified === 'yes'}
              onChange={(e) => handleStaffFormChange('emergencyContactNotified', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {staffForm.emergencyContactNotified === 'yes' && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={intl.formatMessage({ id: 'form.injuryReport.emergencyContactDetails' })}
              placeholder={intl.formatMessage({ id: 'form.injuryReport.placeholderEmergencyContact' })}
              value={staffForm.emergencyContactNotifiedDetails}
              onChange={(e) => handleStaffFormChange('emergencyContactNotifiedDetails', e.target.value)}
            />
          </Grid>
        )}

        {/* Row 9: Hospital Visit */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{intl.formatMessage({ id: 'form.injuryReport.hospitalQuestion' })}</Typography>
            <Switch
              checked={staffForm.hospitalVisitNeeded === 'yes'}
              onChange={(e) => handleStaffFormChange('hospitalVisitNeeded', e.target.checked ? 'yes' : 'no')}
              color="primary"
            />
          </Stack>
        </Grid>

        {staffForm.hospitalVisitNeeded === 'yes' && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={intl.formatMessage({ id: 'form.injuryReport.hospitalDetailsStaff' })}
              value={staffForm.hospitalDetails}
              onChange={(e) => handleStaffFormChange('hospitalDetails', e.target.value)}
            />
          </Grid>
        )}

        {/* Injury Photos Section (Optional) */}
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.injuryPhotos' })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {intl.formatMessage({ id: 'form.injuryReport.injuryPhotosHint' })}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Stack spacing={2}>
            <Button variant="outlined" component="label" startIcon={<CameraOutlined />} sx={{ width: 'fit-content' }}>
              {intl.formatMessage({ id: 'form.injuryReport.uploadPhoto' })}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  const uploaded = await uploadPhotos(files, 'photo');
                  handleStaffFormChange('injuryPhotos', [...staffForm.injuryPhotos, ...uploaded]);
                }}
              />
            </Button>
            {staffForm.injuryPhotos.length > 0 && (
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {staffForm.injuryPhotos.map((photo) => (
                  <Box
                    key={photo.id}
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box
                      component="img"
                      src={photo.preview || photo.url || photo.base64}
                      alt={photo.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'error.lighter' }
                      }}
                      onClick={() => {
                        handleStaffFormChange(
                          'injuryPhotos',
                          staffForm.injuryPhotos.filter((p) => p.id !== photo.id)
                        );
                      }}
                    >
                      <CloseOutlined style={{ fontSize: '0.75rem' }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            {intl.formatMessage({ id: 'form.injuryReport.signatures' })}
          </Typography>
        </Grid>

        {/* Injured Therapist's Signature */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.injuredTherapistSignature' })}
          </Typography>
          <SignaturePad
            signatureRef={therapistSignatureRef}
            onEnd={() => onDataChange && onDataChange()}
            theme={theme}
            signHereLabel={intl.formatMessage({ id: 'form.injuryReport.signHere' })}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <Button size="small" onClick={() => clearSignature(therapistSignatureRef)}>
              {intl.formatMessage({ id: 'form.injuryReport.clearSignature' })}
            </Button>
            <Button size="small" startIcon={<UploadOutlined />} onClick={() => therapistSignatureFileRef.current?.click()}>
              {intl.formatMessage({ id: 'form.injuryReport.uploadSignature' })}
            </Button>
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              ref={therapistSignatureFileRef}
              onChange={() => handleSignatureUpload(therapistSignatureRef, therapistSignatureFileRef)}
            />
          </Stack>
        </Grid>

        {/* Administrator's Signature */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {intl.formatMessage({ id: 'form.injuryReport.adminSignature' })}
          </Typography>
          <SignaturePad
            signatureRef={therapistAdminSignatureRef}
            onEnd={() => onDataChange && onDataChange()}
            theme={theme}
            signHereLabel={intl.formatMessage({ id: 'form.injuryReport.signHere' })}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <Button size="small" onClick={() => clearSignature(therapistAdminSignatureRef)}>
              {intl.formatMessage({ id: 'form.injuryReport.clearSignature' })}
            </Button>
            <Button size="small" startIcon={<UploadOutlined />} onClick={() => therapistAdminSignatureFileRef.current?.click()}>
              {intl.formatMessage({ id: 'form.injuryReport.uploadSignature' })}
            </Button>
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              ref={therapistAdminSignatureFileRef}
              onChange={() => handleSignatureUpload(therapistAdminSignatureRef, therapistAdminSignatureFileRef)}
            />
          </Stack>
        </Grid>
      </Grid>
    </MainCard>
  );

  // Render list view
  // eslint-disable-next-line no-unused-vars
  const renderListView = () => (
    <MainCard
      title={
        <Stack>
          <Typography variant="h5">{intl.formatMessage({ id: 'form.injuryReport.injuryReports' })}</Typography>
          {childName && (
            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage({ id: 'form.injuryReport.forChild' }, { childName })}
            </Typography>
          )}
        </Stack>
      }
      secondary={
        <Button variant="contained" color="primary" startIcon={<PlusOutlined />} onClick={handleNewReport}>
          {intl.formatMessage({ id: 'form.injuryReport.newReport' })}
        </Button>
      }
    >
      {displayedReports.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {intl.formatMessage({ id: 'form.injuryReport.noReportsForChild' }, { childName: childName || '' })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {intl.formatMessage({ id: 'form.injuryReport.clickNewReport' })}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{intl.formatMessage({ id: 'form.injuryReport.dateTime' })}</TableCell>
                <TableCell>{intl.formatMessage({ id: 'form.injuryReport.type' })}</TableCell>
                <TableCell>{intl.formatMessage({ id: 'form.injuryReport.injuredPerson' })}</TableCell>
                <TableCell>{intl.formatMessage({ id: 'form.injuryReport.injuryLocation' })}</TableCell>
                <TableCell>{intl.formatMessage({ id: 'form.injuryReport.firstAid' })}</TableCell>
                <TableCell align="right">{intl.formatMessage({ id: 'form.injuryReport.actions' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedReports.map((report) => (
                <TableRow
                  key={report.id}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        {format(new Date(report.dateOfInjury), 'MMM dd, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(report.timeOfInjury), 'hh:mm a')}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        report.type === 'client'
                          ? intl.formatMessage({ id: 'form.injuryReport.typeChild', defaultMessage: 'Child' })
                          : intl.formatMessage({ id: 'form.injuryReport.typeStaff', defaultMessage: 'Staff' })
                      }
                      size="small"
                      color={report.type === 'client' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{report.injuredName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {report.injuryLocations?.slice(0, 2).map((loc, idx) => (
                        <Chip key={idx} label={loc} size="small" variant="outlined" />
                      ))}
                      {report.injuryLocations?.length > 2 && <Chip label={`+${report.injuryLocations.length - 2}`} size="small" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        report.firstAidAdministered === 'yes'
                          ? intl.formatMessage({ id: 'common.yes' })
                          : intl.formatMessage({ id: 'common.no' })
                      }
                      size="small"
                      color={report.firstAidAdministered === 'yes' ? 'success' : 'default'}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title={intl.formatMessage({ id: 'common.edit' })}>
                        <IconButton size="small" color="primary" onClick={() => handleEditReport(report)}>
                          <EditOutlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={intl.formatMessage({ id: 'common.delete' })}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(report)}>
                          <DeleteOutlined />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>{intl.formatMessage({ id: 'form.injuryReport.deleteTitle' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({ id: 'form.injuryReport.deleteConfirmation' }, { name: reportToDelete?.injuredName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {intl.formatMessage({ id: 'common.delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );

  // Render form view
  const renderFormView = () => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MainCard
        title={
          viewMode === 'individual'
            ? intl.formatMessage({ id: 'form.injuryReport.title' })
            : editingReport
              ? intl.formatMessage({ id: 'form.injuryReport.editTitle' })
              : intl.formatMessage({ id: 'form.injuryReport.newTitle' })
        }
        secondary={
          // Only Back stays in the header — Export and Save live in the
          // bottom action bar of the form so therapists aren't presented with
          // two save buttons that do the same thing.
          viewMode === 'grid' ? (
            <Button variant="outlined" startIcon={<ArrowLeftOutlined />} onClick={handleBackToList}>
              {intl.formatMessage({ id: 'common.back' })}
            </Button>
          ) : null
        }
      >
        <Stack spacing={3}>
          {/* Form Type Selection - only show if not locked to single type */}
          {!singleReportType && (
            <FormControl fullWidth>
              <InputLabel>{intl.formatMessage({ id: 'form.injuryReport.selectReportType' })}</InputLabel>
              <Select
                value={reportType}
                label={intl.formatMessage({ id: 'form.injuryReport.selectReportType' })}
                onChange={handleReportTypeChange}
              >
                <MenuItem value="client">
                  <Stack>
                    <Typography variant="body1">{intl.formatMessage({ id: 'form.injuryReport.childInjuryReport' })}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.injuryReport.childInjuryDesc' })}
                    </Typography>
                  </Stack>
                </MenuItem>
                <MenuItem value="staff">
                  <Stack>
                    <Typography variant="body1">{intl.formatMessage({ id: 'form.injuryReport.therapistInjuryReport' })}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.injuryReport.therapistInjuryDesc' })}
                    </Typography>
                  </Stack>
                </MenuItem>
                <MenuItem value="client-staff">
                  <Stack>
                    <Typography variant="body1">{intl.formatMessage({ id: 'form.injuryReport.childStaffInjury' })}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.injuryReport.childStaffInjuryDesc' })}
                    </Typography>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Render appropriate form(s) based on report type */}
          {(reportType === 'client' || reportType === 'client-staff') && renderClientInjuryForm()}
          {(reportType === 'staff' || reportType === 'client-staff') && renderStaffInjuryForm()}

          {/* Bottom action bar — the only place Save/Export live now that the
              header keeps just Back. Avoids the duplicate-Save confusion. */}
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button variant="outlined" onClick={handleBackToList}>
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button variant="outlined" startIcon={<DownloadOutlined />} onClick={handleExportPDF}>
              {intl.formatMessage({ id: 'form.injuryReport.export' })}
            </Button>
            <Button variant="contained" color="primary" startIcon={<SaveOutlined />} onClick={handleSubmit} disabled={saving}>
              {saving
                ? intl.formatMessage({ id: 'common.saving' })
                : editingReport
                  ? intl.formatMessage({ id: 'form.injuryReport.update' })
                  : intl.formatMessage({ id: 'common.save' })}
            </Button>
          </Stack>
        </Stack>
      </MainCard>
    </LocalizationProvider>
  );

  // Render individual view - card-based list like ABC mode
  const renderIndividualView = () => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={3}>
        <MainCard>
          <Stack spacing={2}>
            {/* Header with New Report Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack>
                <Typography variant="h5">
                  {intl.formatMessage({ id: 'form.injuryReport.injuryReports' })} ({displayedReports.length})
                </Typography>
                {childName && (
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.injuryReport.forChild' }, { childName })}
                  </Typography>
                )}
              </Stack>
              {internalFormMode === 'list' && (
                <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleNewReport}>
                  {intl.formatMessage({ id: 'form.injuryReport.new' })}
                </Button>
              )}
            </Stack>

            {/* Add/Edit Form */}
            {internalFormMode === 'form' && (
              <Paper sx={{ p: 3, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
                <Stack spacing={3}>
                  {/* Form Type Selection - only show if not locked to single type */}
                  {!singleReportType && (
                    <FormControl fullWidth>
                      <InputLabel>{intl.formatMessage({ id: 'form.injuryReport.selectReportType' })}</InputLabel>
                      <Select
                        value={reportType}
                        label={intl.formatMessage({ id: 'form.injuryReport.selectReportType' })}
                        onChange={handleReportTypeChange}
                      >
                        <MenuItem value="client">
                          <Stack>
                            <Typography variant="body1">{intl.formatMessage({ id: 'form.injuryReport.childInjuryReport' })}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.childInjuryDesc' })}
                            </Typography>
                          </Stack>
                        </MenuItem>
                        <MenuItem value="staff">
                          <Stack>
                            <Typography variant="body1">{intl.formatMessage({ id: 'form.injuryReport.therapistInjuryReport' })}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.therapistInjuryDesc' })}
                            </Typography>
                          </Stack>
                        </MenuItem>
                        <MenuItem value="client-staff">
                          <Stack>
                            <Typography variant="body1">{intl.formatMessage({ id: 'form.injuryReport.childStaffInjury' })}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.childStaffInjuryDesc' })}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {/* Render appropriate form(s) based on report type */}
                  {(reportType === 'client' || reportType === 'client-staff') && renderClientInjuryForm()}
                  {(reportType === 'staff' || reportType === 'client-staff') && renderStaffInjuryForm()}

                  {/* Actions */}
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button variant="outlined" onClick={handleBackToList}>
                      {intl.formatMessage({ id: 'common.cancel' })}
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadOutlined />} onClick={handleExportPDF}>
                      {intl.formatMessage({ id: 'form.injuryReport.export' })}
                    </Button>
                    <Button variant="contained" color="primary" startIcon={<SaveOutlined />} onClick={handleSubmit} disabled={saving}>
                      {saving
                        ? intl.formatMessage({ id: 'common.saving' })
                        : editingReport
                          ? intl.formatMessage({ id: 'form.injuryReport.update' })
                          : intl.formatMessage({ id: 'common.save' })}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}

            {/* Reports List - Card Style */}
            {internalFormMode === 'list' && displayedReports.length === 0 ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  {intl.formatMessage(
                    { id: 'form.injuryReport.noReportsYet' },
                    { childName: childName || intl.formatMessage({ id: 'common.thisChild', defaultMessage: 'this child' }) }
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'form.injuryReport.clickNewReportShort' })}
                </Typography>
              </Stack>
            ) : internalFormMode === 'list' ? (
              <Stack spacing={2}>
                {displayedReports.map((report) => (
                  <Paper key={report.id} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
                    <Stack spacing={2}>
                      {/* Header */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle2" fontWeight={600}>
                            {format(new Date(report.dateOfInjury), 'MMM dd, yyyy')} - {format(new Date(report.timeOfInjury), 'hh:mm a')}
                          </Typography>
                          <Chip
                            label={
                              report.type === 'client'
                                ? intl.formatMessage({ id: 'form.injuryReport.childInjury' })
                                : intl.formatMessage({ id: 'form.injuryReport.staffInjury' })
                            }
                            size="small"
                            color={report.type === 'client' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                          <Chip
                            label={
                              report.firstAidAdministered === 'yes'
                                ? intl.formatMessage({ id: 'form.injuryReport.firstAidGiven' })
                                : intl.formatMessage({ id: 'form.injuryReport.noFirstAid' })
                            }
                            size="small"
                            color={report.firstAidAdministered === 'yes' ? 'success' : 'default'}
                            variant="filled"
                          />
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <IconButton size="small" color="primary" onClick={() => handleEditReport(report)}>
                            <EditOutlined />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(report)}>
                            <DeleteOutlined />
                          </IconButton>
                        </Stack>
                      </Stack>

                      {/* Injury Details */}
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Paper sx={{ p: 2, bgcolor: 'primary.lighter', height: '100%' }}>
                            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ mb: 1, display: 'block' }}>
                              {intl.formatMessage({ id: 'form.injuryReport.injuredPersonLabel' })}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {report.injuredName}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Paper sx={{ p: 2, bgcolor: 'warning.lighter', height: '100%' }}>
                            <Typography variant="caption" fontWeight={700} color="warning.main" sx={{ mb: 1, display: 'block' }}>
                              {intl.formatMessage({ id: 'form.injuryReport.injuryLocationLabel' })}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {report.injuryLocations?.join(', ') || intl.formatMessage({ id: 'form.injuryReport.notSpecified' })}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Paper sx={{ p: 2, bgcolor: 'error.lighter', height: '100%' }}>
                            <Typography variant="caption" fontWeight={700} color="error.main" sx={{ mb: 1, display: 'block' }}>
                              {intl.formatMessage({ id: 'form.injuryReport.behaviorLabel' })}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {report.behavior || intl.formatMessage({ id: 'form.injuryReport.notSpecified' })}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Description */}
                      {report.incidentDescription && (
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            {intl.formatMessage({ id: 'form.injuryReport.incidentDescriptionLabel' })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {report.incidentDescription}
                          </Typography>
                        </Paper>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </MainCard>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
          <DialogTitle>{intl.formatMessage({ id: 'form.injuryReport.deleteTitle' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {intl.formatMessage({ id: 'form.injuryReport.deleteConfirmation' }, { name: reportToDelete?.injuredName })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              {intl.formatMessage({ id: 'common.delete' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </LocalizationProvider>
  );

  // Individual view: Show card-based list with inline form (like ABC mode)
  // Grid view: Show table-based list view with ability to switch to form for new/edit
  if (viewMode === 'individual') {
    return renderIndividualView();
  }

  // Grid view - when in form mode, show the form
  if (internalFormMode === 'form') {
    return renderFormView();
  }

  // Grid view with tabs - Data Entry and Historical Data
  // Data Entry tab shows only reports created in this modal session
  // (non-historical). Historical tab shows entries loaded from the backend.
  const dataEntryReports = currentReports;

  // Summary stats across the full displayedReports list.
  const totalReports = displayedReports.length;
  const childReportsCount = displayedReports.filter((r) => r.type === 'client').length;
  const staffReportsCount = displayedReports.filter((r) => r.type === 'staff' || r.type === 'client-staff').length;
  const todaysReportsCount = displayedReports.filter((r) => isToday(r.dateOfInjury)).length;

  return (
    <Stack spacing={3}>
      {/* Summary cards — matches the Behavior Interval modal layout */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="primary.main">
                {totalReports}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.injuryReport.totalReports', defaultMessage: 'Total Reports' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="success.main">
                {todaysReportsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.injuryReport.todaysReports', defaultMessage: "Today's Reports" })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="info.main">
                {childReportsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.injuryReport.childInjuries', defaultMessage: 'Child Injuries' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="secondary.main">
                {staffReportsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.injuryReport.staffInjuries', defaultMessage: 'Staff Injuries' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
      </Grid>

      <MainCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={intl.formatMessage({ id: 'form.injuryReport.dataEntry' })} />
            <Tab label={intl.formatMessage({ id: 'form.injuryReport.historicalData' })} />
          </Tabs>
          <Stack direction="row" spacing={1} sx={{ pr: 2 }}>
            {tabValue === 0 && (
              <Button variant="contained" size="small" startIcon={<PlusOutlined />} onClick={handleNewReport}>
                {intl.formatMessage({ id: 'form.injuryReport.newReport' })}
              </Button>
            )}
          </Stack>
        </Stack>

        <Box sx={{ pt: 3 }}>
          {/* Tab 0: Data Entry — only this modal session's entries. */}
          {tabValue === 0 && (
            <>
              {dataEntryReports.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {intl.formatMessage({ id: 'form.injuryReport.noReportsToday' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.injuryReport.clickNewReport' })}
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{intl.formatMessage({ id: 'form.injuryReport.time' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'form.injuryReport.type' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'form.injuryReport.injuredPerson' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'form.injuryReport.injuryLocation' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'form.injuryReport.firstAid' })}</TableCell>
                        <TableCell align="right">{intl.formatMessage({ id: 'form.injuryReport.actions' })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataEntryReports.map((report) => (
                        <TableRow
                          key={report.id}
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04)
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {format(new Date(report.timeOfInjury), 'hh:mm a')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                report.type === 'client'
                                  ? intl.formatMessage({ id: 'form.injuryReport.typeChild', defaultMessage: 'Child' })
                                  : intl.formatMessage({ id: 'form.injuryReport.typeStaff', defaultMessage: 'Staff' })
                              }
                              size="small"
                              color={report.type === 'client' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{report.injuredName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {report.injuryLocations?.slice(0, 2).map((loc, idx) => (
                                <Chip key={idx} label={loc} size="small" variant="outlined" />
                              ))}
                              {report.injuryLocations?.length > 2 && <Chip label={`+${report.injuryLocations.length - 2}`} size="small" />}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                report.firstAidAdministered === 'yes'
                                  ? intl.formatMessage({ id: 'common.yes' })
                                  : intl.formatMessage({ id: 'common.no' })
                              }
                              size="small"
                              color={report.firstAidAdministered === 'yes' ? 'success' : 'default'}
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title={intl.formatMessage({ id: 'common.view', defaultMessage: 'View' })}>
                                <IconButton size="small" color="primary" onClick={() => handleViewReport(report)}>
                                  <EyeOutlined />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={intl.formatMessage({ id: 'common.delete' })}>
                                <IconButton size="small" color="error" onClick={() => handleDeleteClick(report)}>
                                  <DeleteOutlined />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tab 1: Historical Data - All Reports (Read-only view) */}
          {tabValue === 1 && (
            <>
              {historicalReports.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {intl.formatMessage({ id: 'form.injuryReport.noReportsFound' })}
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{intl.formatMessage({ id: 'form.injuryReport.dateTime' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.injuryReport.type' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.injuryReport.injuredPerson' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.injuryReport.injuryLocation' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.injuryReport.behavior' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.injuryReport.firstAid' })}</TableCell>
                          <TableCell align="right">{intl.formatMessage({ id: 'form.injuryReport.actions' })}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleHistoricalReports.map((report) => (
                          <TableRow
                            key={report.id}
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.08)
                              }
                            }}
                          >
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" fontWeight={500}>
                                  {format(new Date(report.dateOfInjury), 'MMM dd, yyyy')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(report.timeOfInjury), 'hh:mm a')}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  report.type === 'client'
                                    ? intl.formatMessage({ id: 'form.injuryReport.typeChild', defaultMessage: 'Child' })
                                    : intl.formatMessage({ id: 'form.injuryReport.typeStaff', defaultMessage: 'Staff' })
                                }
                                size="small"
                                color={report.type === 'client' ? 'primary' : 'secondary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{report.injuredName}</Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {report.injuryLocations?.slice(0, 2).map((loc, idx) => (
                                  <Chip key={idx} label={loc} size="small" variant="outlined" />
                                ))}
                                {report.injuryLocations?.length > 2 && (
                                  <Chip label={`+${report.injuryLocations.length - 2}`} size="small" />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {report.behavior || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  report.firstAidAdministered === 'yes'
                                    ? intl.formatMessage({ id: 'common.yes' })
                                    : intl.formatMessage({ id: 'common.no' })
                                }
                                size="small"
                                color={report.firstAidAdministered === 'yes' ? 'success' : 'default'}
                                variant="filled"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={intl.formatMessage({ id: 'common.view', defaultMessage: 'View' })}>
                                <IconButton size="small" color="primary" onClick={() => handleViewReport(report)}>
                                  <EyeOutlined />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, px: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage(
                        { id: 'form.injuryReport.historicalShowing', defaultMessage: 'Showing {shown} of {total}' },
                        { shown: visibleHistoricalReports.length, total: historicalReports.length }
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {historicalVisibleCount > HISTORICAL_PAGE_SIZE && (
                        <Button size="small" variant="text" onClick={() => setHistoricalVisibleCount(HISTORICAL_PAGE_SIZE)}>
                          {intl.formatMessage({ id: 'form.injuryReport.showLess', defaultMessage: 'Show less' })}
                        </Button>
                      )}
                      {visibleHistoricalReports.length < historicalReports.length && (
                        <Button size="small" variant="outlined" onClick={() => setHistoricalVisibleCount((n) => n + HISTORICAL_PAGE_SIZE)}>
                          {intl.formatMessage({ id: 'form.injuryReport.loadMore', defaultMessage: 'Load more' })}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </>
              )}
            </>
          )}
        </Box>
      </MainCard>

      {/* Read-only view dialog for historical entries */}
      <Dialog open={Boolean(viewingReport)} onClose={handleCloseView} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              {intl.formatMessage({ id: 'form.injuryReport.viewTitle', defaultMessage: 'Injury Report Details' })}
            </Typography>
            <IconButton onClick={handleCloseView} size="small" aria-label="Close">
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {viewingReport &&
            (() => {
              const raw = viewingReport._rawData || {};
              const isClient = (raw.reportType || viewingReport.type) === 'client';
              const yesNo = (v) => (v === 'yes' ? intl.formatMessage({ id: 'common.yes' }) : intl.formatMessage({ id: 'common.no' }));
              const field = (label, value) => (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              );
              const longField = (label, value) =>
                value ? (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {value}
                    </Typography>
                  </Grid>
                ) : null;
              const sectionTitle = (label) => (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="primary.main" fontWeight={700} sx={{ mt: 1 }}>
                    {label}
                  </Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Grid>
              );

              return (
                <Stack spacing={2.5}>
                  {/* Header chips */}
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={
                        isClient
                          ? intl.formatMessage({ id: 'form.injuryReport.typeChild', defaultMessage: 'Child' })
                          : intl.formatMessage({ id: 'form.injuryReport.typeStaff', defaultMessage: 'Staff' })
                      }
                      color={isClient ? 'primary' : 'secondary'}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`${format(new Date(viewingReport.dateOfInjury), 'MMM dd, yyyy')} · ${format(
                        new Date(viewingReport.timeOfInjury),
                        'hh:mm a'
                      )}`}
                      size="small"
                    />
                  </Stack>

                  <Grid container spacing={2}>
                    {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.basicInfo', defaultMessage: 'Basic Information' }))}
                    {field(intl.formatMessage({ id: 'form.injuryReport.injuredPerson' }), viewingReport.injuredName)}
                    {field(
                      intl.formatMessage({
                        id: isClient ? 'form.injuryReport.staffPresent' : 'form.injuryReport.witnesses',
                        defaultMessage: isClient ? 'Staff Present' : 'Witnesses Present'
                      }),
                      isClient ? raw.staffPresent : raw.witnessesPresent
                    )}

                    {/* Body Diagram */}
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {intl.formatMessage({ id: 'form.injuryReport.injuryLocation' })}
                      </Typography>
                      <Paper variant="outlined" sx={{ mt: 1, p: 1.5 }}>
                        <BodyDiagram selectedParts={viewingReport.injuryLocations || []} disabled />
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                          {(viewingReport.injuryLocations || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            viewingReport.injuryLocations.map((loc, idx) => (
                              <Chip key={`${loc}-${idx}`} label={loc} size="small" variant="outlined" />
                            ))
                          )}
                        </Stack>
                      </Paper>
                    </Grid>

                    {longField(
                      intl.formatMessage({
                        id: 'form.injuryReport.incidentDescription',
                        defaultMessage: 'Incident Description'
                      }),
                      viewingReport.incidentDescription
                    )}

                    {/* ABC + behavior context */}
                    {(raw.antecedent || viewingReport.behavior || raw.consequence || raw.duration) &&
                      sectionTitle(
                        intl.formatMessage({ id: 'form.injuryReport.abcSection', defaultMessage: 'Antecedent / Behavior / Consequence' })
                      )}
                    {longField(intl.formatMessage({ id: 'form.injuryReport.antecedent', defaultMessage: 'Antecedent' }), raw.antecedent)}
                    {longField(intl.formatMessage({ id: 'form.injuryReport.behavior' }), viewingReport.behavior)}
                    {longField(intl.formatMessage({ id: 'form.injuryReport.consequence', defaultMessage: 'Consequence' }), raw.consequence)}
                    {raw.duration &&
                      field(intl.formatMessage({ id: 'form.injuryReport.duration', defaultMessage: 'Duration' }), `${raw.duration} min`)}

                    {/* First aid + medical */}
                    {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.medicalSection', defaultMessage: 'First Aid & Medical' }))}
                    {field(intl.formatMessage({ id: 'form.injuryReport.firstAid' }), yesNo(viewingReport.firstAidAdministered))}
                    {raw.firstAidByWhom &&
                      field(
                        intl.formatMessage({ id: 'form.injuryReport.firstAidByWhom', defaultMessage: 'First Aid By' }),
                        raw.firstAidByWhom
                      )}
                    {field(
                      intl.formatMessage({ id: 'form.injuryReport.hospitalVisit', defaultMessage: 'Hospital Visit Needed' }),
                      yesNo(viewingReport.hospitalVisitNeeded)
                    )}
                    {longField(
                      intl.formatMessage({ id: 'form.injuryReport.hospitalDetails', defaultMessage: 'Hospital Details' }),
                      raw.hospitalDetails
                    )}

                    {/* Notifications */}
                    {(raw.parentNotified || raw.administratorNotified || raw.emergencyContactNotified) &&
                      sectionTitle(intl.formatMessage({ id: 'form.injuryReport.notificationsSection', defaultMessage: 'Notifications' }))}
                    {isClient &&
                      raw.parentNotified &&
                      field(
                        intl.formatMessage({ id: 'form.injuryReport.parentNotified', defaultMessage: 'Parent Notified' }),
                        yesNo(raw.parentNotified)
                      )}
                    {longField(
                      intl.formatMessage({ id: 'form.injuryReport.parentNotifiedDetails', defaultMessage: 'Parent Notification Details' }),
                      raw.parentNotifiedDetails
                    )}
                    {!isClient &&
                      raw.administratorNotified &&
                      field(
                        intl.formatMessage({ id: 'form.injuryReport.adminNotified', defaultMessage: 'Administrator Notified' }),
                        yesNo(raw.administratorNotified)
                      )}
                    {!isClient &&
                      raw.emergencyContactNotified &&
                      field(
                        intl.formatMessage({
                          id: 'form.injuryReport.emergencyContactNotified',
                          defaultMessage: 'Emergency Contact Notified'
                        }),
                        yesNo(raw.emergencyContactNotified)
                      )}
                    {longField(
                      intl.formatMessage({
                        id: 'form.injuryReport.emergencyContactDetails',
                        defaultMessage: 'Emergency Contact Details'
                      }),
                      raw.emergencyContactNotifiedDetails
                    )}

                    {/* Environment / Damage */}
                    {raw.environmentDamage === 'yes' &&
                      sectionTitle(
                        intl.formatMessage({ id: 'form.injuryReport.environmentSection', defaultMessage: 'Environment / Property Damage' })
                      )}
                    {raw.environmentDamage === 'yes' &&
                      longField(
                        intl.formatMessage({
                          id: 'form.injuryReport.environmentDamageDescription',
                          defaultMessage: 'Description'
                        }),
                        // The form writes the description to environmentDamageDetails;
                        // fall back to the legacy key for older records.
                        raw.environmentDamageDetails || raw.environmentDamageDescription
                      )}
                    {raw.environmentDamage === 'yes' &&
                      longField(
                        intl.formatMessage({ id: 'form.injuryReport.propertyDamaged', defaultMessage: 'Property Damaged' }),
                        raw.propertyDamaged
                      )}

                    {/* Photos */}
                    {Array.isArray(raw.injuryPhotos) && raw.injuryPhotos.length > 0 && (
                      <>
                        {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.photosSection', defaultMessage: 'Injury Photos' }))}
                        <Grid size={12}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {raw.injuryPhotos.map((photo, idx) => {
                              // Photos are saved as { id, path, url, name } when uploaded online or
                              // { id, base64, name } when offline. Fall back through every possible
                              // shape (including a plain string URL) so older entries still display.
                              const photoSrc = typeof photo === 'string' ? photo : photo?.url || photo?.base64 || photo?.preview || '';
                              if (!photoSrc) return null;
                              return (
                                <Box
                                  key={photo?.id || idx}
                                  component="img"
                                  src={photoSrc}
                                  alt={photo?.name || `Injury photo ${idx + 1}`}
                                  sx={{
                                    width: 120,
                                    height: 120,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: 'divider'
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </Grid>
                      </>
                    )}

                    {/* Signatures */}
                    {(raw.adminSignature || raw.parentSignature || raw.therapistSignature || raw.injuredTherapistSignature) && (
                      <>
                        {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.signaturesSection', defaultMessage: 'Signatures' }))}
                        {raw.adminSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.adminSignature', defaultMessage: 'Administrator' })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.adminSignature}
                              alt="Administrator signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                        {raw.parentSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.parentSignature', defaultMessage: 'Parent / Guardian' })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.parentSignature}
                              alt="Parent signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                        {raw.therapistSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.therapistSignature', defaultMessage: 'Therapist' })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.therapistSignature}
                              alt="Therapist signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                        {raw.injuredTherapistSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({
                                id: 'form.injuryReport.injuredTherapistSignature',
                                defaultMessage: 'Injured Therapist'
                              })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.injuredTherapistSignature}
                              alt="Injured therapist signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Stack>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView}>{intl.formatMessage({ id: 'common.close' })}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>{intl.formatMessage({ id: 'form.injuryReport.deleteTitle' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({ id: 'form.injuryReport.deleteConfirmation' }, { name: reportToDelete?.injuredName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {intl.formatMessage({ id: 'common.delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

InjuryReportMode.propTypes = {
  viewMode: PropTypes.oneOf(['individual', 'grid']),
  onDataChange: PropTypes.func,
  childName: PropTypes.string,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  abcRecords: PropTypes.array,
  therapistName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultReportType: PropTypes.oneOf(['client', 'staff', 'client-staff']),
  filterByType: PropTypes.oneOf(['client', 'staff', null]),
  singleReportType: PropTypes.oneOf(['client', 'staff', null]),
  initialReports: PropTypes.array
};

export default InjuryReportMode;
